import { api, http } from '../client';
import type { UUID } from '@/types/domain';
import type {
  AdmissionApplication,
  AdmissionApplicationInput,
  AdmissionDocument,
  AdmissionEnrolmentResult,
  AdmissionSearchParams,
  EnrolApplicantInput,
} from '@/types/admission';

/*
 * The admissions module's endpoints.
 *
 * Every transition is its own endpoint, and the server decides whether it is
 * open from the application's current status. The screens offer actions; they
 * do not work out which are legal. A refusal arrives as a message to show as
 * it is.
 */

export const admissionsApi = {
  /** Search matches applicant, guardian and application number — on the server. */
  list: (params: AdmissionSearchParams) =>
    api.getPage<AdmissionApplication>('/admissions', { params }),

  get: (id: UUID) => api.get<AdmissionApplication>(`/admissions/${id}`),
  create: (payload: AdmissionApplicationInput) =>
    api.post<AdmissionApplication>('/admissions', payload),

  /** Only while SUBMITTED, UNDER_REVIEW or ASSESSMENT_SCHEDULED. */
  update: (id: UUID, payload: AdmissionApplicationInput) =>
    api.put<AdmissionApplication>(`/admissions/${id}`, payload),

  startReview: (id: UUID) => api.post<AdmissionApplication>(`/admissions/${id}/review`),

  /** `at` is an ISO instant. */
  scheduleAssessment: (id: UUID, at: string, notes?: string) =>
    api.post<AdmissionApplication>(`/admissions/${id}/assessment`, undefined, {
      params: { at, notes },
    }),

  recordAssessment: (id: UUID, notes: string) =>
    api.post<AdmissionApplication>(`/admissions/${id}/assessment/notes`, undefined, {
      params: { notes },
    }),

  offer: (id: UUID, note?: string) =>
    api.post<AdmissionApplication>(`/admissions/${id}/offer`, undefined, { params: { note } }),

  accept: (id: UUID) => api.post<AdmissionApplication>(`/admissions/${id}/accept`),

  /** Reason required. */
  reject: (id: UUID, reason: string) =>
    api.post<AdmissionApplication>(`/admissions/${id}/reject`, undefined, { params: { reason } }),

  withdraw: (id: UUID, reason?: string) =>
    api.post<AdmissionApplication>(`/admissions/${id}/withdraw`, undefined, {
      params: { reason },
    }),

  /**
   * Creates the student, the enrolment and any admission-fee invoice in one
   * step, and reports each id it produced.
   */
  enrol: (id: UUID, payload: EnrolApplicantInput) =>
    api.post<AdmissionEnrolmentResult>(`/admissions/${id}/enrol`, payload),

  documents: (id: UUID) => api.get<AdmissionDocument[]>(`/admissions/${id}/documents`),
  uploadDocument: (id: UUID, file: File, documentType: string) =>
    uploadDocument(`/admissions/${id}/documents`, file, documentType),
  downloadDocument: (id: UUID, documentId: UUID, fileName: string) =>
    downloadFile(`/admissions/${id}/documents/${documentId}/download`, fileName),
  deleteDocument: (id: UUID, documentId: UUID) =>
    api.delete(`/admissions/${id}/documents/${documentId}`),
};

// ----------------------------------------------------------------- internal

/**
 * Upload a file with its type as a query parameter.
 *
 * The outer Content-Type is left undefined so the browser sets
 * multipart/form-data with its boundary.
 */
async function uploadDocument(
  path: string,
  file: File,
  documentType: string,
): Promise<AdmissionDocument> {
  const form = new FormData();
  form.append('file', file);

  const response = await http.post<{ data: AdmissionDocument }>(path, form, {
    params: { documentType },
    headers: { 'Content-Type': undefined },
  });
  return response.data.data;
}

/**
 * Fetch a file and hand the browser a save prompt.
 *
 * Fetched as a blob rather than opened as a link: the request needs an
 * Authorization header, and a plain anchor cannot carry one.
 */
async function downloadFile(path: string, filename: string) {
  const response = await http.get(path, { responseType: 'blob' });

  const url = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
